import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Spinner, Alert, Row, Col } from 'react-bootstrap';
import { bulkUpdateMusica, getSupportiMusica, getCondizioniMusica, getFornitori } from '../services/api';

const BulkEditMusicaModal = ({ show, onHide, selectedIds, onSuccess }) => {
  const [updates, setUpdates] = useState({});
  const [fieldsToUpdate, setFieldsToUpdate] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Data for dropdowns
  const [supporti, setSupporti] = useState([]);
  const [condizioni, setCondizioni] = useState([]);
  const [fornitori, setFornitori] = useState([]);

  useEffect(() => {
    if (show) {
      const fetchDropdownData = async () => {
        try {
          const [supportiRes, condizioniRes, fornitoriRes] = await Promise.all([
            getSupportiMusica(),
            getCondizioniMusica(),
            getFornitori(0, 100000),
          ]);
          setSupporti(['', ...supportiRes.data]);
          setCondizioni(condizioniRes.data);
          setFornitori(fornitoriRes.data.fornitori);
        } catch (err) {
          console.error("Errore nel caricamento dei dati per i dropdown:", err);
          setError("Errore nel caricamento dei dati per i menu a tendina.");
        }
      };
      fetchDropdownData();
      
      // Reset state on open
      setUpdates({});
      setFieldsToUpdate({});
      setError(null);
    }
  }, [show]);
  
  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFieldsToUpdate(prev => ({ ...prev, [name]: checked }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUpdates(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);

    const finalUpdates = {};
    for (const field in fieldsToUpdate) {
      if (fieldsToUpdate[field]) {
        if (updates[field] === '') {
          finalUpdates[field] = null;
        } else {
          finalUpdates[field] = updates[field];
        }
      }
    }

    // Convert boolean fields
    const booleanFields = ['in_vetrina', 'fuori_catalogo'];
    for (const field of booleanFields) {
      if (field in finalUpdates && finalUpdates[field] !== null) {
        if (finalUpdates[field] === 'true') {
          finalUpdates[field] = true;
        } else if (finalUpdates[field] === 'false') {
          finalUpdates[field] = false;
        }
      }
    }

    if (Object.keys(finalUpdates).length === 0) {
      setError("Nessun campo selezionato per l'aggiornamento.");
      setLoading(false);
      return;
    }

    try {
      await bulkUpdateMusica(selectedIds, finalUpdates);
      onSuccess();
      onHide();
    } catch (err) {
      setError(err.response?.data?.detail || "Si è verificato un errore durante l'aggiornamento.");
    } finally {
      setLoading(false);
    }
  };

  const renderField = (name, label, type = 'text', options = []) => (
    <Form.Group as={Row} className="mb-3 align-items-center" controlId={`formBulk${name}`}>
      <Col sm={1}>
        <Form.Check
          type="checkbox"
          name={name}
          checked={fieldsToUpdate[name] || false}
          onChange={handleCheckboxChange}
        />
      </Col>
      <Col sm={3}>
        <Form.Label>{label}</Form.Label>
      </Col>
      <Col sm={8}>
        {type === 'select' ? (
          <Form.Select
            name={name}
            onChange={handleInputChange}
            disabled={!fieldsToUpdate[name]}
            value={updates[name] || ''}
          >
            <option value="">Seleziona...</option>
            {options.map((option, index) => (
              <option key={`${name}-${index}-${option.value ?? option}`} value={option.value ?? option}>
                {option.label || option}
              </option>
            ))}
          </Form.Select>
        ) : (
          <Form.Control
            type={type}
            name={name}
            value={updates[name] || ''}
            onChange={handleInputChange}
            disabled={!fieldsToUpdate[name]}
            step={type === 'number' ? '0.01' : undefined}
          />
        )}
      </Col>
    </Form.Group>
  );

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Modifica Massiva Musica ({selectedIds.length} selezionati)</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>Spunta la casella accanto ai campi che desideri aggiornare per tutti i prodotti musicali selezionati.</p>
        <hr />
        {error && <Alert variant="danger">{error}</Alert>}
        
        <Form>
          {renderField('prezzo_negozio', 'Prezzo Negozio', 'number')}
          {renderField('prezzo_ibs', 'Prezzo IBS', 'number')}
          {renderField('costo_acquisto', 'Costo Acquisto', 'number')}
          {renderField('supporto', 'Supporto', 'select', supporti)}
          {renderField('condizione', 'Condizione', 'select', condizioni)}
          {renderField('fornitore_id', 'Fornitore', 'select', fornitori.map(f => ({ value: f.id, label: f.denominazione })))}
          {renderField('fuori_catalogo', 'Fuori Catalogo', 'select', [
            { value: true, label: 'Sì' },
            { value: false, label: 'No' }
          ])}
          {renderField('in_vetrina', 'In Vetrina', 'select', [
            { value: true, label: 'Sì' },
            { value: false, label: 'No' }
          ])}
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Annulla
        </Button>
        <Button variant="primary" onClick={handleSubmit} disabled={loading}>
          {loading ? <Spinner as="span" animation="border" size="sm" /> : 'Applica Modifiche'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default BulkEditMusicaModal;